import { useEffect } from 'react';

import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { IExtractionListResponse } from '../../api/types';
import { IExtraction } from '../../types/my-types';
import useGlynt from './useGlynt';
import { getOffsetFromUrl } from '../../utils/getOffsetFromUrl';

function mergeResults(data: InfiniteData<IExtractionListResponse> | undefined) {
  if (!data) return [];

  let results: IExtraction[] = [];

  data?.pages?.map((page) => {
    results = results.concat(page.results);
    return true;
  });

  return results;
}

// get all extractions from an extraction batch
export default function useGetAllExtractions(extractionBatchId: string | undefined, dpid?: string) {
  const glynt = useGlynt(dpid);

  const limit = 150;

  const { data, isError, isLoading, isFetching, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ['allExtractions', extractionBatchId],
      queryFn: async ({ pageParam }) => {
        return (await glynt).extraction.list({
          advanced: true,
          limit,
          offset: pageParam,
          extraction_batch: extractionBatchId as string,
        });
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage.next) {
          const offset = getOffsetFromUrl(lastPage.next);
          return offset;
        }
      },
      refetchOnWindowFocus: false,
      enabled: Boolean(extractionBatchId),
    });

  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [data, hasNextPage, fetchNextPage]);

  const currentPage = data?.pages?.length ?? 0;
  const totalPages = data?.pages?.[0]?.count ? Math.ceil(data.pages[0].count / limit) : 0;

  const totalExtractions = data?.pages?.reduce((acc, page) => acc + page.results.length, 0);

  const ebSummarySuccessful = data?.pages?.reduce<IExtraction[]>((acc, page) => {
    return acc.concat(
      page.results.filter(
        (result) => result.status === 'Success' && result.data_processing_status === 'Success'
      )
    );
  }, []);

  const failedExtractions = data?.pages?.reduce<IExtraction[]>((acc, page) => {
    return acc.concat(page.results.filter((result) => result.status === 'Failed'));
  }, []);

  const ebSummaryFailed = data?.pages?.reduce<IExtraction[]>((acc, page) => {
    return acc.concat(
      page.results.filter(
        (result) => result.data_processing_status === 'Failed' || result.status === 'Failed'
      )
    );
  }, []);

  const failedDataProcessingStatus = data?.pages?.reduce<IExtraction[]>((acc, page) => {
    return acc.concat(page.results.filter((result) => result.data_processing_status === 'Failed'));
  }, []);

  const ebSummaryTotalExtractions = totalExtractions || 0;

  return {
    isLoading,
    isError,
    isFetching,
    data: mergeResults(data),
    currentPage,
    totalPages,
    totalExtractionsCount: ebSummaryTotalExtractions,
    ebSummarySuccessful: ebSummarySuccessful?.length || 0,
    ebSummaryFailed: ebSummaryFailed?.length || 0,
    ebSummaryInProgress:
      ebSummaryTotalExtractions -
      (ebSummarySuccessful?.length || 0) -
      (ebSummaryFailed?.length || 0),
    failedExtractions: (failedExtractions || []).concat(failedDataProcessingStatus || []),
    failedDataProcessingStatus: failedDataProcessingStatus || [],
    refetch,
  };
}
